<template>
  <div class="nr1-saved-dashboard animated fadeIn">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="mb-1">{{ dashboard ? dashboard.name : "Dashboard" }}</h3>
        <p class="text-muted mb-0">
          Consolidação automática das respostas dos questionários selecionados.
        </p>
      </div>
      <b-button variant="secondary" @click="$router.push('/nr1/questionarios')">
        <i class="fa fa-arrow-left mr-2"></i> Voltar
      </b-button>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-3 text-muted">Carregando dashboard...</p>
    </div>

    <b-alert v-else-if="error" variant="danger" show>
      {{ error }}
    </b-alert>

    <template v-else-if="dashboard">
      <b-card class="saved-dashboard-card mb-4">
        <div class="saved-dashboard-meta">
          <div>
            <div class="saved-dashboard-label">Questionários selecionados</div>
            <div class="saved-dashboard-count">
              {{ dashboard.questionnaireIds.length }} questionário(s)
            </div>
          </div>
          <div class="saved-dashboard-tags">
            <span
              v-for="item in dashboard.questionnaires || []"
              :key="item._id"
              class="saved-dashboard-tag"
            >
              {{ item.title }}
            </span>
          </div>
        </div>
      </b-card>

      <Nr1Dashboard
        :filter-company-id="dashboard.company"
        :fixed-questionnaire-ids="dashboard.questionnaireIds"
        :hide-questionnaire-filter="true"
        @filters-change="handleDashboardFiltersChange"
      >
        <template #after-area-risk>
          <b-col cols="12">
            <div class="saved-dashboard-details mb-3">
              <div v-if="detailsLoading" class="text-center py-5">
                <b-spinner variant="primary"></b-spinner>
                <p class="mt-3 text-muted mb-0">
                  Carregando análises detalhadas...
                </p>
              </div>

              <b-alert v-else-if="detailsError" variant="danger" show>
                {{ detailsError }}
              </b-alert>

              <template v-else>
                <b-row>
                  <b-col lg="6">
                    <b-card
                      no-body
                      class="dashboard-card chart-animation mb-4 detail-chart-card"
                    >
                      <b-card-body class="detail-chart-card-body">
                        <h6 class="mb-0">
                          Matriz de Calor (Severidade x Probabilidade)
                        </h6>
                        <div class="risk-matrix-container">
                          <div class="y-axis-label">Severidade</div>
                          <div class="matrix-grid">
                            <div
                              class="matrix-row"
                              v-for="s in [4, 3, 2, 1, 0]"
                              :key="`saved-s${s}`"
                            >
                              <div class="s-label">{{ s + 1 }}</div>
                              <div
                                v-for="p in [0, 1, 2, 3, 4]"
                                :key="`saved-p${s}-${p}`"
                                class="matrix-cell"
                                :style="{ backgroundColor: getCellColor(s, p) }"
                                @click="selectQuadrant(s, p)"
                                :class="{
                                  'has-data': heatMapMatrix[s][p].count > 0,
                                }"
                              >
                                <span
                                  v-if="heatMapMatrix[s][p].count > 0"
                                  class="risk-count"
                                >
                                  {{ heatMapMatrix[s][p].count }}
                                </span>
                              </div>
                            </div>
                            <div class="matrix-row labels-row">
                              <div class="s-label"></div>
                              <div
                                class="p-label"
                                v-for="option in probabilityLabels"
                                :key="`saved-pl${option.value}`"
                              >
                                {{ option.label }}
                              </div>
                            </div>
                            <div class="x-axis-label text-center mt-2">
                              Probabilidade
                            </div>
                          </div>
                        </div>
                        <div
                          v-if="selectedQuadrantItems"
                          class="mt-3 quadrant-details"
                        >
                          <div
                            class="d-flex justify-content-between align-items-center mb-2"
                          >
                            <h6 class="mb-0 small font-weight-bold">
                              Itens Selecionados no Quadrante:
                            </h6>
                            <b-button
                              size="sm"
                              variant="link"
                              class="p-0"
                              @click="selectedQuadrantItems = null"
                              >Limpar</b-button
                            >
                          </div>
                          <div
                            class="list-group list-group-flush border rounded"
                            style="max-height: 200px; overflow-y: auto"
                          >
                            <div
                              v-for="(item, idx) in selectedQuadrantItems"
                              :key="`quadrant-${idx}`"
                              class="list-group-item small p-2"
                            >
                              <strong>{{ item.area }}</strong
                              >: {{ item.categoryName }} -
                              {{ item.questionText || "Pergunta" }} (Score:
                              {{ formatScore(item.riskScore) }})
                            </div>
                          </div>
                        </div>
                      </b-card-body>
                    </b-card>
                  </b-col>

                  <b-col lg="6">
                    <b-card
                      no-body
                      class="dashboard-card chart-animation mb-4 detail-chart-card"
                    >
                      <b-card-body class="detail-chart-card-body">
                        <h6 class="mb-0">Radar de Riscos por Categoria</h6>
                        <apexchart
                          type="radar"
                          height="460"
                          :options="radarOptions"
                          :series="radarSeries"
                        ></apexchart>
                      </b-card-body>
                    </b-card>
                  </b-col>
                </b-row>

                <b-tabs content-class="mt-3">
                  <b-tab title="Perigos Identificados" active>
                    <div class="details-surface">
                      <b-table
                        :items="areaRisks"
                        :fields="areaFields"
                        hover
                        responsive
                        class="nr1-table mb-0"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                      >
                        <template #cell(area)="data">
                          <strong>{{ data.value }}</strong>
                          <br /><small class="text-muted"
                            >{{ data.item.totalResponses }} respostas</small
                          >
                        </template>
                        <template #cell(overallRiskScore)="data">
                          <b-badge
                            :variant="
                              getRiskVariant(data.item.overallRiskLevel)
                            "
                            class="px-3 py-2"
                          >
                            {{ formatScore(data.value) }}
                          </b-badge>
                        </template>
                        <template #cell(overallRiskLevel)="data">
                          <span
                            class="font-weight-bold"
                            :class="`text-${getRiskVariant(data.value)}`"
                            >{{ data.value }}</span
                          >
                        </template>
                        <template #cell(actions)="data">
                          <b-button
                            size="sm"
                            variant="outline-info"
                            @click="viewAreaDetails(data.item)"
                          >
                            <i class="fa fa-list"></i> Detalhes
                          </b-button>
                        </template>
                      </b-table>
                    </div>
                  </b-tab>

                  <b-tab title="Planos de Ação (5W2H)">
                    <div
                      class="d-flex justify-content-between align-items-center mb-3"
                    >
                      <h6 class="mb-0">
                        Cronograma de Medidas Preventivas e Corretivas
                      </h6>
                      <div class="d-flex align-items-center">
                        <b-button
                          v-if="selectedActionPlanIds.length"
                          variant="success"
                          size="sm"
                          class="mr-2"
                          @click="markSelectedActionPlansAsCompleted"
                        >
                          <i class="fa fa-check"></i> Marcar como concluído
                        </b-button>
                        <b-button
                          variant="primary"
                          size="sm"
                          @click="openActionPlanModal(null)"
                        >
                          <i class="fa fa-plus"></i> Novo Plano
                        </b-button>
                      </div>
                    </div>

                    <div class="details-surface">
                      <b-table
                        :items="actionPlans"
                        :fields="actionPlanFields"
                        hover
                        responsive
                        class="nr1-table mb-0"
                        empty-text="Nenhum plano de ação encontrado para este dashboard"
                      >
                        <template #cell(select)="data">
                          <b-form-checkbox
                            :checked="isActionPlanSelected(data.item._id)"
                            @change="
                              toggleActionPlanSelection(data.item._id, $event)
                            "
                          ></b-form-checkbox>
                        </template>
                        <template #cell(when)="data">{{
                          formatDate(data.value)
                        }}</template>
                        <template #cell(status)="data">
                          <b-badge :variant="getActionPlanVariant(data.value)">
                            {{ data.value }}
                          </b-badge>
                        </template>
                        <template #cell(item_actions)="data">
                          <b-button-group size="sm">
                            <b-button
                              variant="link"
                              class="text-info p-0 mr-2"
                              @click="openActionPlanModal(data.item)"
                            >
                              <i class="fa fa-edit"></i>
                            </b-button>
                            <b-button
                              variant="link"
                              class="text-danger p-0"
                              @click="removeActionPlan(data.item._id)"
                            >
                              <i class="fa fa-trash"></i>
                            </b-button>
                          </b-button-group>
                        </template>
                      </b-table>
                    </div>
                  </b-tab>
                </b-tabs>
              </template>
            </div>
          </b-col>
        </template>
      </Nr1Dashboard>

      <b-modal
        v-model="showAreaDetailsModal"
        title="Detalhes da Área"
        size="xl"
        hide-footer
        @hidden="selectedArea = null"
      >
        <div v-if="selectedArea">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 class="mb-1">{{ selectedArea.area }}</h5>
              <small class="text-muted"
                >{{ selectedArea.totalResponses || 0 }} respostas</small
              >
            </div>
            <div class="text-right">
              <div class="mb-1">
                <small class="text-muted mr-2">Score médio:</small>
                <b-badge
                  :variant="getRiskVariant(selectedArea.overallRiskLevel)"
                >
                  {{ formatScore(selectedArea.overallRiskScore) }}
                </b-badge>
              </div>
              <div>
                <small class="text-muted mr-2">Nível:</small>
                <strong
                  :class="`text-${getRiskVariant(
                    selectedArea.overallRiskLevel
                  )}`"
                >
                  {{ selectedArea.overallRiskLevel }}
                </strong>
              </div>
            </div>
          </div>

          <b-table
            :items="selectedArea.risks || []"
            :fields="areaDetailFields"
            responsive
            hover
            class="nr1-table mb-0"
          >
            <template #cell(riskScore)="data">
              <b-badge :variant="getRiskVariant(data.item.riskLevel)">
                {{ formatScore(data.value) }}
              </b-badge>
            </template>
            <template #cell(riskLevel)="data">
              <span
                :class="`text-${getRiskVariant(data.value)}`"
                class="font-weight-bold"
              >
                {{ data.value }}
              </span>
            </template>
            <template #cell(action)="data">
              <b-button
                size="sm"
                variant="link"
                @click="
                  openActionPlanModal(
                    null,
                    selectedArea.area,
                    data.item.categoryId,
                    resolveQuestionnaireForCategory(data.item.categoryId)
                  )
                "
              >
                <i class="fa fa-plus-circle"></i> Planejar
              </b-button>
            </template>
          </b-table>
        </div>
      </b-modal>

      <b-modal
        v-model="showActionPlanModal"
        title="Plano de Ação 5W2H"
        @ok="handleActionPlanOk"
        size="lg"
        ok-title="Salvar"
        cancel-title="Voltar"
      >
        <b-form>
          <b-row>
            <b-col md="6">
              <b-form-group label="Questionário (Obrigatório)">
                <b-form-select
                  v-model="newActionPlan.questionnaire"
                  :options="actionPlanQuestionnaireOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Área (Obrigatório)">
                <b-form-input
                  v-model="newActionPlan.area"
                  placeholder="Ex: Produção"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Categoria de risco (Obrigatório)">
                <b-form-select
                  v-model="newActionPlan.categoryId"
                  :options="actionPlanCategoryOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Status">
                <b-form-select
                  v-model="newActionPlan.status"
                  :options="actionPlanStatusOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="O que (What) - Medida Corretiva/Preventiva">
                <b-form-input
                  v-model="newActionPlan.what"
                  placeholder="Ex: Treinamento em NR-10"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Por que (Why) - Justificativa">
                <b-form-textarea
                  v-model="newActionPlan.why"
                  rows="2"
                ></b-form-textarea>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Como (How) - Método de execução">
                <b-form-textarea
                  v-model="newActionPlan.how"
                  rows="2"
                ></b-form-textarea>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Onde (Where) - Setor">
                <b-form-input v-model="newActionPlan.where"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Quem (Who) - Responsável">
                <b-form-input v-model="newActionPlan.who"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Quando (When) - Prazo">
                <b-form-datepicker
                  v-model="newActionPlan.when"
                  locale="pt-BR"
                  :date-format-options="{
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }"
                  :value-as-date="false"
                  placeholder="dd/mm/aaaa"
                ></b-form-datepicker>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Quanto (How Much) - Custo">
                <b-form-input
                  v-model="newActionPlan.howMuch"
                  placeholder="R$ 0,00"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-modal>
    </template>
  </div>
</template>

<script>
import {
  getDashboard,
  getMacroDashboardAnalytics,
  getQuestionnaire,
  createActionPlan,
  updateActionPlan,
  deleteActionPlan,
} from "@/api/nr1";
import Nr1Dashboard from "../dashboard/modules/nr1-dashboard.vue";
import VueApexCharts from "vue-apexcharts";
import moment from "moment";

export default {
  name: "NR1SavedDashboard",
  components: {
    Nr1Dashboard,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      loading: false,
      error: "",
      dashboard: null,
      activeDashboardFilters: null,
      detailsLoading: false,
      detailsError: "",
      analytics: null,
      questionnaireDetails: [],
      selectedArea: null,
      showAreaDetailsModal: false,
      selectedQuadrantItems: null,
      selectedActionPlanIds: [],
      showActionPlanModal: false,
      editingActionPlan: null,
      newActionPlan: {
        questionnaire: "",
        what: "",
        why: "",
        where: "",
        when: "",
        who: "",
        how: "",
        howMuch: "",
        status: "Pendente",
        area: "",
        categoryId: "",
      },
      sortBy: "overallRiskScore",
      sortDesc: true,
      areaFields: [
        { key: "area", label: "Área", sortable: true },
        {
          key: "totalResponses",
          label: "Respostas",
          sortable: true,
          class: "text-center",
        },
        {
          key: "overallRiskScore",
          label: "Score Médio",
          sortable: true,
          class: "text-center",
        },
        {
          key: "overallRiskLevel",
          label: "Nível de Risco",
          sortable: true,
          class: "text-center",
        },
        { key: "actions", label: "Ações", class: "text-center" },
      ],
      actionPlanFields: [
        {
          key: "select",
          label: "",
          class: "text-center",
          thStyle: { width: "42px" },
          tdClass: "px-1 text-center",
        },
        { key: "what", label: "O que" },
        { key: "area", label: "Onde (Setor)" },
        { key: "who", label: "Quem" },
        { key: "questionnaireTitle", label: "Questionário" },
        { key: "when", label: "Quando" },
        { key: "status", label: "Status" },
        { key: "item_actions", label: "Ações" },
      ],
      areaDetailFields: [
        { key: "categoryName", label: "Categoria" },
        {
          key: "avgProbability",
          label: "Resposta Média",
          class: "text-center",
        },
        { key: "severity", label: "Gravidade", class: "text-center" },
        { key: "riskScore", label: "Score", class: "text-center" },
        { key: "riskLevel", label: "Nível", class: "text-center" },
        { key: "action", label: "Ação", class: "text-center" },
      ],
      heatMapMatrix: Array(5)
        .fill()
        .map(() => Array(5).fill({ count: 0, items: [] })),
      probabilityLabels: [
        { value: 1, label: "Nunca" },
        { value: 2, label: "Raramente" },
        { value: 3, label: "Às vezes" },
        { value: 4, label: "Frequentemente" },
        { value: 5, label: "Sempre" },
      ],
    };
  },
  computed: {
    areaRisks() {
      return this.analytics?.areaRisks || [];
    },
    actionPlans() {
      return this.analytics?.actionPlans || [];
    },
    actionPlanQuestionnaireOptions() {
      return [
        { value: "", text: "Selecione o questionário" },
        ...this.questionnaireDetails.map((item) => ({
          value: String(item._id),
          text: item.title,
        })),
      ];
    },
    selectedActionPlanQuestionnaire() {
      const questionnaireId = parseInt(this.newActionPlan.questionnaire, 10);
      if (Number.isNaN(questionnaireId)) {
        return null;
      }

      return (
        this.questionnaireDetails.find(
          (item) => item._id === questionnaireId
        ) || null
      );
    },
    actionPlanCategoryOptions() {
      const categories = Array.isArray(
        this.selectedActionPlanQuestionnaire?.categories
      )
        ? this.selectedActionPlanQuestionnaire.categories
        : [];

      return [
        { value: "", text: "Selecione a categoria" },
        ...categories.map((category) => ({
          value: String(category.id),
          text: category.name,
        })),
      ];
    },
    actionPlanStatusOptions() {
      return ["Pendente", "Em Andamento", "Concluído", "Cancelado"];
    },
    radarOptions() {
      return {
        chart: { type: "radar", toolbar: { show: false } },
        plotOptions: {
          radar: {
            size: 190,
            polygons: {
              strokeColors: "#e2e8f0",
              connectorColors: "#e2e8f0",
            },
          },
        },
        xaxis: {
          categories: (this.analytics?.categoryRisks || []).map(
            (item) => item.categoryName
          ),
          labels: {
            style: {
              fontSize: "11px",
            },
          },
        },
        yaxis: { max: 25 },
        stroke: { width: 2 },
        fill: { opacity: 0.4 },
        colors: ["#00E396"],
        markers: { size: 5 },
        dataLabels: {
          enabled: false,
        },
        legend: { show: false },
      };
    },
    radarSeries() {
      return [
        {
          name: "Score de Risco",
          data: (this.analytics?.categoryRisks || []).map(
            (item) => item.riskScore
          ),
        },
      ];
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.loadDashboard();
      },
    },
  },
  methods: {
    async loadDashboard() {
      this.loading = true;
      this.error = "";

      try {
        const response = await getDashboard(this.$route.params.id);
        this.dashboard = response.data;
        await this.loadQuestionnaireDetails();
      } catch (error) {
        console.error("Error loading saved dashboard:", error);
        this.error =
          error.response?.data?.message || "Erro ao carregar dashboard salvo";
      } finally {
        this.loading = false;
      }
    },
    async loadQuestionnaireDetails() {
      const questionnaireIds = Array.isArray(this.dashboard?.questionnaireIds)
        ? this.dashboard.questionnaireIds
        : [];

      if (!questionnaireIds.length) {
        this.questionnaireDetails = [];
        return;
      }

      const responses = await Promise.all(
        questionnaireIds.map((id) => getQuestionnaire(id))
      );

      this.questionnaireDetails = responses
        .map((response) => response.data)
        .filter(Boolean);
    },
    buildAnalyticsRequestParams() {
      const params = {
        company: this.dashboard?.company,
        questionnaireIds: this.dashboard?.questionnaireIds?.join(",") || "",
      };

      if (!this.activeDashboardFilters) {
        return params;
      }

      if (this.activeDashboardFilters.dateMode) {
        params.dateMode = this.activeDashboardFilters.dateMode;
      }
      if (this.activeDashboardFilters.dateFrom) {
        params.dateFrom = this.activeDashboardFilters.dateFrom;
      }
      if (this.activeDashboardFilters.dateTo) {
        params.dateTo = this.activeDashboardFilters.dateTo;
      }
      if (this.activeDashboardFilters.area) {
        params.area = this.activeDashboardFilters.area;
      }
      if (this.activeDashboardFilters.businessUnit) {
        params.businessUnit = this.activeDashboardFilters.businessUnit;
      }

      return params;
    },
    async handleDashboardFiltersChange(filters) {
      const nextFilters = filters ? { ...filters } : null;
      const hasChanged =
        JSON.stringify(this.activeDashboardFilters) !==
        JSON.stringify(nextFilters);

      this.activeDashboardFilters = nextFilters;

      if (hasChanged && this.dashboard?.questionnaireIds?.length) {
        await this.loadDetailedAnalytics();
      }
    },
    async loadDetailedAnalytics() {
      if (!this.dashboard?.questionnaireIds?.length) {
        this.analytics = null;
        this.selectedActionPlanIds = [];
        this.buildHeatMap();
        return;
      }

      this.detailsLoading = true;
      this.detailsError = "";

      try {
        const response = await getMacroDashboardAnalytics(
          this.buildAnalyticsRequestParams()
        );

        this.analytics = response.data;
        this.selectedQuadrantItems = null;
        const availableIds = new Set(
          (response.data?.actionPlans || []).map((actionPlan) =>
            String(actionPlan._id)
          )
        );
        this.selectedActionPlanIds = this.selectedActionPlanIds.filter((id) =>
          availableIds.has(id)
        );
        this.buildHeatMap();
      } catch (error) {
        console.error("Error loading saved dashboard analytics:", error);
        this.detailsError =
          error.response?.data?.message ||
          "Erro ao carregar análises detalhadas do dashboard";
      } finally {
        this.detailsLoading = false;
      }
    },
    buildHeatMap() {
      const matrix = Array(5)
        .fill()
        .map(() => Array(5).fill({ count: 0, items: [] }));

      (this.analytics?.matrix || []).forEach((cell) => {
        const severityIndex = Number(cell.severity) - 1;
        const probabilityIndex = Number(cell.probability) - 1;

        if (
          severityIndex >= 0 &&
          severityIndex < 5 &&
          probabilityIndex >= 0 &&
          probabilityIndex < 5
        ) {
          matrix[severityIndex][probabilityIndex] = {
            count: cell.count,
            items: cell.items || [],
          };
        }
      });

      this.heatMapMatrix = matrix;
    },
    getCellColor(severityIndex, probabilityIndex) {
      const score = (severityIndex + 1) * (probabilityIndex + 1);
      if (score >= 16) return "#ffcdd2";
      if (score >= 11) return "#ffe0b2";
      if (score >= 6) return "#fff9c4";
      return "#c8e6c9";
    },
    selectQuadrant(severityIndex, probabilityIndex) {
      this.selectedQuadrantItems =
        this.heatMapMatrix[severityIndex][probabilityIndex].items;
    },
    viewAreaDetails(item) {
      this.selectedArea = item;
      this.showAreaDetailsModal = true;
    },
    handleActionPlanOk(event) {
      event.preventDefault();
      this.saveActionPlan();
    },
    isActionPlanSelected(actionPlanId) {
      return this.selectedActionPlanIds.includes(String(actionPlanId));
    },
    toggleActionPlanSelection(actionPlanId, selected) {
      const normalizedId = String(actionPlanId);
      if (selected) {
        if (!this.isActionPlanSelected(normalizedId)) {
          this.selectedActionPlanIds.push(normalizedId);
        }
        return;
      }

      this.selectedActionPlanIds = this.selectedActionPlanIds.filter(
        (id) => id !== normalizedId
      );
    },
    resolveQuestionnaireForCategory(categoryId) {
      const matches = this.questionnaireDetails.filter((questionnaire) =>
        Array.isArray(questionnaire.categories)
          ? questionnaire.categories.some(
              (category) => String(category.id) === String(categoryId)
            )
          : false
      );

      return matches.length === 1 ? String(matches[0]._id) : "";
    },
    openActionPlanModal(
      item,
      area = null,
      categoryId = null,
      questionnaireId = ""
    ) {
      this.editingActionPlan = item;
      if (item) {
        this.newActionPlan = {
          ...item,
          questionnaire: String(item.questionnaire || ""),
          when: moment(item.when).format("YYYY-MM-DD"),
          categoryId: String(item.categoryId || ""),
        };
      } else {
        this.newActionPlan = {
          questionnaire: String(questionnaireId || ""),
          what: "",
          why: "",
          where: area || "",
          when: "",
          who: "",
          how: "",
          howMuch: "",
          status: "Pendente",
          area: area || "",
          categoryId: categoryId && questionnaireId ? String(categoryId) : "",
        };
      }
      this.showActionPlanModal = true;
    },
    validateActionPlan() {
      const validators = [
        {
          valid: !!String(this.newActionPlan.questionnaire || "").trim(),
          message: "Selecione o questionário do plano de ação.",
        },
        {
          valid: !!String(this.newActionPlan.area || "").trim(),
          message: "Informe a área do plano de ação.",
        },
        {
          valid: !!String(this.newActionPlan.categoryId || "").trim(),
          message: "Selecione a categoria de risco.",
        },
        {
          valid: !!String(this.newActionPlan.what || "").trim(),
          message: "Informe o que será feito.",
        },
        {
          valid: !!this.newActionPlan.when,
          message: "Informe o prazo do plano de ação.",
        },
        {
          valid: !!String(this.newActionPlan.who || "").trim(),
          message: "Informe o responsável pelo plano.",
        },
      ];

      const invalidField = validators.find((item) => !item.valid);
      if (invalidField) {
        this.$bvToast.toast(invalidField.message, {
          title: "Validação",
          variant: "warning",
          solid: true,
        });
        return false;
      }

      return true;
    },
    async saveActionPlan() {
      if (!this.validateActionPlan()) {
        return;
      }

      const payload = {
        ...this.newActionPlan,
        questionnaire: Number(this.newActionPlan.questionnaire),
        categoryId: String(this.newActionPlan.categoryId),
      };

      try {
        if (this.editingActionPlan) {
          await updateActionPlan(this.editingActionPlan._id, payload);
        } else {
          await createActionPlan(payload);
        }

        this.showActionPlanModal = false;
        this.editingActionPlan = null;
        await this.loadDetailedAnalytics();
        this.$bvToast.toast("Plano de ação salvo com sucesso.", {
          title: "Sucesso",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error("Erro ao salvar plano:", error);
        const errorMessage =
          error?.response?.data?.message ||
          "Não foi possível salvar o plano de ação.";

        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      }
    },
    async removeActionPlan(id) {
      if (!confirm("Deseja remover este plano de ação?")) {
        return;
      }

      try {
        await deleteActionPlan(id);
        await this.loadDetailedAnalytics();
      } catch (error) {
        console.error("Erro ao remover plano:", error);
        this.$bvToast.toast(
          error?.response?.data?.message ||
            "Não foi possível remover o plano de ação.",
          {
            title: "Erro",
            variant: "danger",
            solid: true,
          }
        );
      }
    },
    async markSelectedActionPlansAsCompleted() {
      if (!this.selectedActionPlanIds.length) {
        return;
      }

      if (!confirm("Marcar os planos selecionados como concluídos?")) {
        return;
      }

      try {
        await Promise.all(
          this.selectedActionPlanIds.map((id) =>
            updateActionPlan(id, {
              status: "Concluído",
            })
          )
        );

        this.selectedActionPlanIds = [];
        await this.loadDetailedAnalytics();
        this.$bvToast.toast("Planos selecionados concluídos com sucesso.", {
          title: "Sucesso",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error("Erro ao concluir planos selecionados:", error);
        this.$bvToast.toast(
          error?.response?.data?.message ||
            "Não foi possível concluir os planos selecionados.",
          {
            title: "Erro",
            variant: "danger",
            solid: true,
          }
        );
      }
    },
    formatDate(date) {
      if (!date) {
        return "-";
      }

      return moment.utc(date).format("DD/MM/YYYY");
    },
    formatScore(value) {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue.toFixed(1) : "0.0";
    },
    getRiskVariant(level) {
      const variants = {
        Crítico: "danger",
        Alto: "warning",
        Médio: "info",
        Baixo: "success",
      };

      return variants[level] || "secondary";
    },
    getActionPlanVariant(status) {
      if (status === "Concluído") return "success";
      if (status === "Pendente") return "warning";
      if (status === "Em Andamento") return "info";
      if (status === "Cancelado") return "secondary";
      return "secondary";
    },
  },
};
</script>

<style scoped>
.saved-dashboard-details {
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.saved-dashboard-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.details-surface {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.saved-dashboard-meta {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.saved-dashboard-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  font-weight: 700;
}

.saved-dashboard-count {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
}

.saved-dashboard-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.saved-dashboard-tag {
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.risk-matrix-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.y-axis-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-weight: bold;
  margin-right: 15px;
  color: #666;
}

.matrix-grid {
  display: flex;
  flex-direction: column;
}

.matrix-row {
  display: flex;
  flex-direction: row;
}

.matrix-cell {
  width: 60px;
  height: 60px;
  border: 1px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.matrix-cell:hover {
  filter: brightness(0.9);
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.matrix-cell.has-data {
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.1);
}

.risk-count {
  font-weight: bold;
  font-size: 1.2rem;
  color: #333;
}

.s-label,
.p-label {
  width: 60px;
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #888;
  font-size: 0.62rem;
  line-height: 1.1;
  text-align: center;
  padding: 0.15rem;
}

.s-label {
  width: 30px;
  height: 60px;
  margin-right: 10px;
}

.labels-row {
  margin-top: 10px;
}

.dashboard-card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
  overflow: hidden;
}

.detail-chart-card {
  min-height: 560px;
}

.detail-chart-card-body {
  min-height: 560px;
  display: flex;
  flex-direction: column;
  border-radius: inherit;
}

.nr1-table ::v-deep thead th {
  color: #718096;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  border-top: none;
}

.nr1-table ::v-deep tbody td {
  vertical-align: middle;
}

.badge {
  font-size: 0.85rem;
  font-weight: 600;
}

@media (max-width: 991px) {
  .risk-matrix-container {
    padding: 12px 0;
    overflow-x: auto;
    justify-content: flex-start;
  }

  .detail-chart-card,
  .detail-chart-card-body {
    min-height: auto;
  }
}
</style>
